import { useState, useEffect } from 'react';

/**
 * Hook for managing service worker registration and updates
 */
export function useServiceWorker() {
  // Check if we're running in a browser environment
  const isBrowser = typeof window !== 'undefined' && typeof navigator !== 'undefined';
  
  // Initialize state only if in browser
  const [isServiceWorkerRegistered, setIsServiceWorkerRegistered] = useState(false);
  const [waitingServiceWorker, setWaitingServiceWorker] = useState(null);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Only run in browser environment
    if (!isBrowser) return;
    
    // Check if service workers are supported
    if (!('serviceWorker' in navigator)) {
      setError('Service workers are not supported in this browser.');
      return;
    }

    // Dynamically import Workbox to prevent SSR issues
    const registerServiceWorker = async () => {
      try {
        const { Workbox } = await import('workbox-window');
        const wb = new Workbox('/sw.js');

        // Add event listeners for service worker updates
        wb.addEventListener('waiting', (event) => {
          setIsUpdateAvailable(true);
          setWaitingServiceWorker(event.sw);
        });

        wb.addEventListener('controlling', () => {
          // When the service worker is controlling, reload the page
          window.location.reload();
        });

        wb.addEventListener('activated', () => {
          // When the service worker is activated, clear any update notifications
          setIsUpdateAvailable(false);
          setWaitingServiceWorker(null);
        });

        // Register the service worker
        const reg = await wb.register();
        console.log('Service worker registered successfully');
        setRegistration(reg);
        setIsServiceWorkerRegistered(true);
      } catch (err) {
        console.error('Error setting up service worker:', err);
        setError(`Error setting up service worker: ${err.message}`);
      }
    };

    registerServiceWorker();

    // Cleanup function
    return () => {
      // Nothing to clean up as service worker registration persists
    };
  }, [isBrowser]); // Dependency on isBrowser

  /**
   * Function to update the service worker
   */
  const updateServiceWorker = () => {
    if (waitingServiceWorker) {
      // Send a message to the waiting service worker to skip waiting
      waitingServiceWorker.postMessage({ type: 'SKIP_WAITING' });
    }
  };

  /**
   * Function to send a message to the service worker
   * @param {Object} message - The message to send
   * @returns {Promise<any>} The response from the service worker
   */
  const messageSW = async (message) => {
    if (!registration || !registration.active) {
      throw new Error('No active service worker found');
    }

    return new Promise((resolve, reject) => {
      const messageChannel = new MessageChannel();
      
      messageChannel.port1.onmessage = (event) => {
        if (event.data.error) {
          reject(event.data.error);
        } else {
          resolve(event.data);
        }
      };

      registration.active.postMessage(message, [messageChannel.port2]);
    });
  };

  return {
    isServiceWorkerRegistered,
    isUpdateAvailable,
    updateServiceWorker,
    messageSW,
    error
  };
} 